import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, PlayerPosition } from '@hokm/shared';

/**
 * Layout zones for UI positioning.
 * Supports dynamic aspect ratios with a 16:9 core game area.
 */
export interface LayoutZones {
  /** Core 16:9 game area (where gameplay happens) */
  gameArea: { x: number; y: number; width: number; height: number };
  /** Scoreboard position */
  scoreboard: { x: number; y: number };
  /** Player label positions for each display position */
  playerLabels: Record<PlayerPosition, { x: number; y: number }>;
  /** Available margins outside the game area */
  margins: { left: number; right: number; top: number; bottom: number };
  /** Whether viewport is in portrait orientation */
  isPortrait: boolean;
  /** Current viewport dimensions */
  viewport: { width: number; height: number };
  /** Scale factor applied to game area */
  scale: number;
  /** Offset to add to game coordinates to get screen coordinates */
  offset: { x: number; y: number };
}

/**
 * LayoutManager handles viewport detection and dynamic layout calculations.
 * Uses RESIZE mode to fill the viewport while maintaining a centered 16:9 game area.
 */
export class LayoutManager {
  private static instance: LayoutManager | null = null;
  private game: Phaser.Game | null = null;
  private orientationWarning: HTMLDivElement | null = null;
  private resizeListener: (() => void) | null = null;
  private currentLayout: LayoutZones | null = null;
  private onLayoutChangeCallbacks: ((layout: LayoutZones) => void)[] = [];

  private constructor() {}

  static getInstance(): LayoutManager {
    if (!LayoutManager.instance) {
      LayoutManager.instance = new LayoutManager();
    }
    return LayoutManager.instance;
  }

  /**
   * Initialize the layout manager with the Phaser game instance.
   * Call this after game creation.
   */
  initialize(game: Phaser.Game): void {
    this.game = game;
    this.setupResizeListener();
    this.recalculateLayout();
    this.checkOrientation();
  }

  /**
   * Clean up listeners and DOM elements.
   */
  destroy(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      window.removeEventListener('orientationchange', this.resizeListener);
      this.resizeListener = null;
    }
    if (this.orientationWarning) {
      this.orientationWarning.remove();
      this.orientationWarning = null;
    }
    this.onLayoutChangeCallbacks = [];
    this.game = null;
    LayoutManager.instance = null;
  }

  /**
   * Register a callback for layout changes.
   */
  onLayoutChange(callback: (layout: LayoutZones) => void): void {
    this.onLayoutChangeCallbacks.push(callback);
  }

  /**
   * Remove a layout change callback.
   */
  offLayoutChange(callback: (layout: LayoutZones) => void): void {
    this.onLayoutChangeCallbacks = this.onLayoutChangeCallbacks.filter((cb) => cb !== callback);
  }

  /**
   * Calculate layout zones based on viewport dimensions.
   */
  calculateLayout(viewportWidth: number, viewportHeight: number): LayoutZones {
    const isPortrait = viewportHeight > viewportWidth * 1.1;

    // Calculate scale to fit 16:9 game area
    const gameAspect = GAME_WIDTH / GAME_HEIGHT;
    const viewportAspect = viewportWidth / viewportHeight;

    let scale: number;
    let offsetX: number;
    let offsetY: number;

    if (viewportAspect > gameAspect) {
      // Viewport is wider than 16:9 - letterbox on sides
      scale = viewportHeight / GAME_HEIGHT;
      offsetX = (viewportWidth - GAME_WIDTH * scale) / 2;
      offsetY = 0;
    } else {
      // Viewport is taller than 16:9 - letterbox on top/bottom
      scale = viewportWidth / GAME_WIDTH;
      offsetX = 0;
      offsetY = (viewportHeight - GAME_HEIGHT * scale) / 2;
    }

    const scaledWidth = GAME_WIDTH * scale;
    const scaledHeight = GAME_HEIGHT * scale;

    // Calculate margins (extra space outside the 16:9 game area)
    const margins = {
      left: offsetX,
      right: viewportWidth - offsetX - scaledWidth,
      top: offsetY,
      bottom: viewportHeight - offsetY - scaledHeight,
    };

    // Calculate scoreboard position (top-right of game area, or in right margin if wide enough)
    const scoreboardX = margins.right > 150 ? GAME_WIDTH + margins.right / scale / 2 : GAME_WIDTH - 80;
    const scoreboardY = margins.top > 100 ? -margins.top / scale / 2 + 80 : 80;

    // Calculate player label positions
    // These are in game coordinates (will be transformed by camera)
    const centerX = GAME_WIDTH / 2;
    const centerY = GAME_HEIGHT / 2 - 30;

    const playerLabels: Record<PlayerPosition, { x: number; y: number }> = {
      [PlayerPosition.SOUTH]: { x: centerX, y: GAME_HEIGHT - 190 },
      [PlayerPosition.WEST]: { x: 150, y: centerY },
      [PlayerPosition.NORTH]: { x: centerX, y: 155 },
      [PlayerPosition.EAST]: { x: GAME_WIDTH - 150, y: centerY },
    };

    // If we have extra side margins, we can move labels outward
    if (margins.left > 100) {
      playerLabels[PlayerPosition.WEST].x = -margins.left / scale / 2 + 80;
    }
    if (margins.right > 100) {
      playerLabels[PlayerPosition.EAST].x = GAME_WIDTH + margins.right / scale / 2 - 80;
    }

    return {
      gameArea: {
        x: offsetX,
        y: offsetY,
        width: scaledWidth,
        height: scaledHeight,
      },
      scoreboard: { x: scoreboardX, y: scoreboardY },
      playerLabels,
      margins,
      isPortrait,
      viewport: { width: viewportWidth, height: viewportHeight },
      scale,
      offset: { x: offsetX, y: offsetY },
    };
  }

  /**
   * Get current layout (cached).
   */
  getLayout(): LayoutZones {
    if (!this.currentLayout) {
      this.recalculateLayout();
    }
    return this.currentLayout!;
  }

  /**
   * Force recalculation of layout.
   */
  recalculateLayout(): LayoutZones {
    const viewport = this.getViewportSize();
    this.currentLayout = this.calculateLayout(viewport.width, viewport.height);
    return this.currentLayout;
  }

  /**
   * Check if viewport is in portrait mode.
   */
  isPortraitMode(): boolean {
    const viewport = this.getViewportSize();
    return viewport.height > viewport.width * 1.1;
  }

  /**
   * Get current viewport dimensions.
   */
  getViewportSize(): { width: number; height: number } {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }

  /**
   * Convert game coordinates to screen coordinates.
   */
  gameToScreen(gameX: number, gameY: number): { x: number; y: number } {
    const layout = this.getLayout();
    return {
      x: gameX * layout.scale + layout.offset.x,
      y: gameY * layout.scale + layout.offset.y,
    };
  }

  /**
   * Convert screen coordinates to game coordinates.
   */
  screenToGame(screenX: number, screenY: number): { x: number; y: number } {
    const layout = this.getLayout();
    return {
      x: (screenX - layout.offset.x) / layout.scale,
      y: (screenY - layout.offset.y) / layout.scale,
    };
  }

  /**
   * Set up resize listener for viewport/orientation changes.
   */
  private setupResizeListener(): void {
    this.resizeListener = () => {
      this.recalculateLayout();
      this.checkOrientation();
      this.notifyLayoutChange();
    };
    window.addEventListener('resize', this.resizeListener);
    window.addEventListener('orientationchange', this.resizeListener);
  }

  /**
   * Notify all registered callbacks of layout change.
   */
  private notifyLayoutChange(): void {
    if (this.currentLayout) {
      this.onLayoutChangeCallbacks.forEach((cb) => cb(this.currentLayout!));
    }
  }

  /**
   * Check orientation and show/hide warning accordingly.
   */
  private checkOrientation(): void {
    if (this.isPortraitMode()) {
      this.showOrientationWarning();
    } else {
      this.hideOrientationWarning();
    }
  }

  /**
   * Show portrait orientation warning overlay.
   */
  private showOrientationWarning(): void {
    if (this.orientationWarning) return;

    this.orientationWarning = document.createElement('div');
    this.orientationWarning.id = 'orientation-warning';
    this.orientationWarning.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(10, 42, 26, 0.95);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        color: #f4e8d3;
        font-family: Georgia, serif;
        text-align: center;
        padding: 20px;
        box-sizing: border-box;
      ">
        <div style="
          font-size: 48px;
          margin-bottom: 20px;
          animation: rotate-icon 1.5s ease-in-out infinite;
        ">📱</div>
        <h2 style="
          font-size: 24px;
          margin: 0 0 15px 0;
          color: #d4af37;
        ">Rotate Your Device</h2>
        <p style="
          font-size: 16px;
          color: #c0b090;
          max-width: 280px;
          line-height: 1.5;
        ">Hokm plays best in landscape mode. Please rotate your device for the optimal experience.</p>
      </div>
      <style>
        @keyframes rotate-icon {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-15deg); }
          75% { transform: rotate(90deg); }
        }
      </style>
    `;

    document.body.appendChild(this.orientationWarning);

    // Pause the game if possible
    if (this.game?.scene.scenes.length) {
      this.game.scene.scenes.forEach((scene) => {
        if (scene.scene.isActive()) {
          scene.scene.pause();
        }
      });
    }
  }

  /**
   * Hide portrait orientation warning overlay.
   */
  private hideOrientationWarning(): void {
    if (!this.orientationWarning) return;

    this.orientationWarning.remove();
    this.orientationWarning = null;

    // Resume the game if it was paused
    if (this.game?.scene.scenes.length) {
      this.game.scene.scenes.forEach((scene) => {
        if (scene.scene.isPaused()) {
          scene.scene.resume();
        }
      });
    }
  }
}

/**
 * Convenience function to calculate layout.
 */
export function calculateLayout(viewportWidth: number, viewportHeight: number): LayoutZones {
  return LayoutManager.getInstance().calculateLayout(viewportWidth, viewportHeight);
}

/**
 * Convenience function to get current layout.
 */
export function getLayout(): LayoutZones {
  return LayoutManager.getInstance().getLayout();
}

/**
 * Check if currently in portrait mode.
 */
export function isPortraitMode(): boolean {
  return LayoutManager.getInstance().isPortraitMode();
}
